// frontend/src/components/ExpenseForm.tsx

import React, { useEffect, useState, FormEvent } from "react";
import {
  X,
  ReceiptText,
  FileText,
  BadgeDollarSign,
  Percent,
  CalendarDays,
  BookOpen,
  UserRound,
  UsersRound,
} from "lucide-react";
import {
  CreateExpenseRequest,
  SubcategoryRef,
} from "../types/expense";
import { getSubcategories } from "../api/subcategories";
import { getTeachers } from "../api/teachers";
import { getStaff } from "../api/staff";
import { getCourses } from "../api/courses";

type PayToType = "teacher" | "staff" | "";

interface TeacherOption {
  id: string;
  name: string;
  courses: {
    id: string;
    name: string;
  }[];
}

interface Props {
  onSubmit: (data: CreateExpenseRequest) => Promise<void>;
  onCancel: () => void;
}

export default function ExpenseForm({
  onSubmit,
  onCancel,
}: Props) {
  const [subs, setSubs] = useState<SubcategoryRef[]>([]);
  const [teachers, setTeachers] = useState<TeacherOption[]>([]);
  const [staff, setStaff] = useState<
    { id: string; name: string }[]
  >([]);
  const [courses, setCourses] = useState<
    { id: string; name: string }[]
  >([]);
  const [error, setError] = useState("");

  const [form, setForm] = useState({
    subcategory: "",
    remarks: "",
    amount: "",
    taxRate: "",
    date: new Date().toISOString().slice(0, 10),
    teacher: "",
    staff: "",
    course: "",
  });

  const [payToType, setPayToType] = useState<PayToType>("");
  const [taxAmount, setTaxAmount] = useState(0);

  useEffect(() => {
    getSubcategories().then(setSubs);

    getTeachers().then((ts) =>
      setTeachers(
        ts.map((t) => ({
          id: t.id,
          name: t.name,
          courses: t.courses.map((c) => ({
            id: c.id,
            name: c.name,
          })),
        })),
      ),
    );

    getStaff().then((ss) =>
      setStaff(
        ss.map((s) => ({
          id: s.id,
          name: s.name,
        })),
      ),
    );

    getCourses().then((cs) =>
      setCourses(
        cs.map((c) => ({
          id: c.id,
          name: c.name,
        })),
      ),
    );
  }, []);

  // Recompute tax amount when amount or taxRate changes
  useEffect(() => {
    const amt = Number(form.amount) || 0;
    const tax = Number(form.taxRate) || 0;

    setTaxAmount((amt * tax) / 100);
  }, [form.amount, form.taxRate]);

  // Clear payTo whenever subcategory changes
  useEffect(() => {
    setPayToType("");
    setForm((f) => ({
      ...f,
      teacher: "",
      staff: "",
    }));
  }, [form.subcategory]);

  const selected = subs.find(
    (s) => s.id === form.subcategory,
  );

  const selName =
    selected?.name.toLowerCase();

  const isTaxable =
    selected?.categoryType === "taxable" ||
    selected?.categoryType === "vat-refundable";

  function handleChange(
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement
    >,
  ) {
    const { name, value } = e.target;

    if (name === "payToType") {
      setPayToType(
        value as PayToType,
      );
    } else {
      setForm((f) => ({
        ...f,
        [name]:
          name === "amount"
            ? value.replace(
                /[^0-9.]/g,
                "",
              )
            : name === "taxRate"
              ? value.replace(
                  /[^0-9]/g,
                  "",
                )
              : value,
      }));
    }
  }

  async function handleSubmit(
    e: FormEvent,
  ) {
    e.preventDefault();
    setError("");

    if (!form.subcategory) {
      setError(
        "Category is required",
      );
      return;
    }

    if (selName === "salary") {
      if (!payToType) {
        setError(
          "Choose Teacher or Staff",
        );
        return;
      }

      if (
        payToType === "teacher" &&
        !form.teacher
      ) {
        setError(
          "Select a teacher",
        );
        return;
      }

      if (
        payToType === "staff" &&
        !form.staff
      ) {
        setError(
          "Select a staff",
        );
        return;
      }

      if (
        payToType === "teacher" &&
        !form.course
      ) {
        setError(
          "Select a course for salary",
        );
        return;
      }
    }

    if (
      selName === "consultant"
    ) {
      if (!form.teacher) {
        setError(
          "Select a teacher for consultant",
        );
        return;
      }

      if (!form.course) {
        setError(
          "Select a course for consultant",
        );
        return;
      }
    }

    const payload: CreateExpenseRequest = {
      subcategory:
        form.subcategory,
      remarks:
        form.remarks ||
        undefined,
      amount:
        Number(
          form.amount,
        ) || 0,
      date: form.date,
    };

    if (isTaxable) {
      payload.taxRate =
        Number(
          form.taxRate,
        ) || 0;
    }

    if (
      selName === "salary"
    ) {
      if (
        payToType ===
        "teacher"
      ) {
        payload.teacher =
          form.teacher;
        payload.course =
          form.course;
      } else {
        payload.staff =
          form.staff;
      }
    } else if (
      selName ===
      "consultant"
    ) {
      payload.teacher =
        form.teacher;
      payload.course =
        form.course;
    } else if (
      form.course
    ) {
      payload.course =
        form.course;
    }

    try {
      await onSubmit(
        payload,
      );
    } catch (err: any) {
      setError(
        err.response?.data
          ?.message ||
          err.message,
      );
    }
  }

  const fieldClass = `
    box-border h-11 w-full min-w-0 rounded-[10px]
    border border-slate-200 bg-white px-3.5
    text-[13px] font-medium text-slate-800
    outline-none transition
    placeholder:text-slate-400
    hover:border-slate-300
    focus:border-emerald-400
    focus:ring-2 focus:ring-emerald-100
  `;

  const labelClass = `
    mb-1.5 block
    text-[10px] font-semibold uppercase
    tracking-[0.05em] text-slate-500
  `;

  const iconClass =
    "pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400";

  const selectedTeacher =
    teachers.find(
      (t) =>
        t.id ===
        form.teacher,
    );

  return (
    <div
      className="
        fixed inset-0 z-[1600]
        flex items-center justify-center
        bg-slate-950/45 px-4 py-6
        backdrop-blur-[2px]
      "
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="expense-form-title"
        className="
          flex max-h-[92vh] w-full max-w-[820px]
          flex-col overflow-hidden
          rounded-[18px]
          border border-slate-200
          bg-white
          shadow-[0_24px_70px_rgba(15,23,42,0.22)]
        "
      >
        {/* Header */}
        <div
          className="
            flex shrink-0 items-start justify-between gap-4
            border-b border-slate-100
            px-6 py-5
            max-sm:px-4
          "
        >
          <div className="min-w-0">
            <p
              className="
                m-0 text-[10px] font-bold uppercase
                tracking-[0.12em] text-blue-600
              "
            >
              Expense management
            </p>

            <h2
              id="expense-form-title"
              className="
                mb-0 mt-1
                text-[20px] font-bold
                tracking-[-0.02em]
                text-slate-950
              "
            >
              New Expense
            </h2>

            <p className="mb-0 mt-1 text-[12px] leading-5 text-slate-500">
              Record expense details, tax information and payment allocation.
            </p>
          </div>

          <button
            type="button"
            onClick={onCancel}
            aria-label="Close expense form"
            title="Close"
            className="
              inline-flex h-9 w-9 shrink-0 items-center justify-center
              rounded-[9px]
              border border-emerald-600
              bg-emerald-600
              text-white transition
              hover:border-emerald-700
              hover:bg-emerald-700
              focus:outline-none
              focus:ring-2
              focus:ring-emerald-200
            "
          >
            <X size={17} />
          </button>
        </div>

        {error && (
          <div
            className="
              mx-6 mt-4 rounded-[10px]
              border border-red-200 bg-red-50
              px-4 py-3
              text-[12px] font-medium text-red-700
              max-sm:mx-4
            "
          >
            {error}
          </div>
        )}

        <form
          onSubmit={
            handleSubmit
          }
          className="min-h-0 flex-1 overflow-y-auto"
        >
          <div
            className="
              grid grid-cols-1 gap-x-4 gap-y-4
              px-6 py-5
              sm:grid-cols-2
              max-sm:px-4
            "
          >
            {/* Category */}
            <label className="min-w-0">
              <span className={labelClass}>
                Category
              </span>

              <div className="relative">
                <ReceiptText
                  size={15}
                  className={iconClass}
                />

                <select
                  id="subcategory"
                  name="subcategory"
                  value={
                    form.subcategory
                  }
                  onChange={
                    handleChange
                  }
                  required
                  className={`${fieldClass} pl-10 pr-8`}
                >
                  <option value="">
                    Select…
                  </option>

                  {subs.map((s) => (
                    <option
                      key={s.id}
                      value={s.id}
                    >
                      {s.name} (
                      {
                        s.categoryType
                      }
                      )
                    </option>
                  ))}
                </select>
              </div>
            </label>

            {/* Date */}
            <label className="min-w-0">
              <span className={labelClass}>
                Date
              </span>

              <div className="relative">
                <CalendarDays
                  size={15}
                  className={iconClass}
                />

                <input
                  id="date"
                  name="date"
                  type="date"
                  value={
                    form.date
                  }
                  onChange={
                    handleChange
                  }
                  required
                  className={`${fieldClass} pl-10`}
                />
              </div>
            </label>

            {/* Remarks */}
            <label className="min-w-0 sm:col-span-2">
              <span className={labelClass}>
                Remarks
              </span>

              <div className="relative">
                <FileText
                  size={15}
                  className={iconClass}
                />

                <input
                  id="remarks"
                  name="remarks"
                  type="text"
                  value={
                    form.remarks
                  }
                  onChange={
                    handleChange
                  }
                  placeholder="Add remarks"
                  className={`${fieldClass} pl-10`}
                />
              </div>
            </label>

            {/* Amount */}
            <label className="min-w-0">
              <span className={labelClass}>
                Amount
              </span>

              <div className="relative">
                <BadgeDollarSign
                  size={15}
                  className={iconClass}
                />

                <input
                  id="amount"
                  name="amount"
                  type="text"
                  inputMode="decimal"
                  pattern="[0-9.]*"
                  min="0"
                  value={
                    form.amount
                  }
                  onChange={
                    handleChange
                  }
                  required
                  autoComplete="off"
                  placeholder="Enter amount"
                  className={`${fieldClass} pl-10`}
                />
              </div>
            </label>

            {/* Tax */}
            {isTaxable && (
              <label className="min-w-0">
                <span className={labelClass}>
                  Tax Rate %
                </span>

                <div className="relative">
                  <Percent
                    size={15}
                    className={iconClass}
                  />

                  <input
                    id="taxRate"
                    name="taxRate"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    min="0"
                    max="100"
                    value={
                      form.taxRate
                    }
                    onChange={
                      handleChange
                    }
                    required
                    autoComplete="off"
                    placeholder="Enter tax rate"
                    className={`${fieldClass} pl-10`}
                  />
                </div>
              </label>
            )}

            {isTaxable && (
              <div className="sm:col-span-2">
                <div
                  className="
                    flex items-center justify-between gap-4
                    rounded-[12px]
                    border border-emerald-100
                    bg-emerald-50/70
                    px-4 py-3
                  "
                >
                  <span className="text-[12px] font-medium text-slate-600">
                    Tax Amount
                  </span>

                  <span className="text-[14px] font-bold text-emerald-700">
                    Rs{" "}
                    {taxAmount.toFixed(
                      2,
                    )}
                  </span>
                </div>
              </div>
            )}

            {/* Course if normal expense */}
            {selName !==
              "salary" &&
              selName !==
                "consultant" && (
                <label className="min-w-0">
                  <span className={labelClass}>
                    Course
                  </span>

                  <div className="relative">
                    <BookOpen
                      size={15}
                      className={iconClass}
                    />

                    <select
                      id="course"
                      name="course"
                      value={
                        form.course
                      }
                      onChange={
                        handleChange
                      }
                      className={`${fieldClass} pl-10 pr-8`}
                    >
                      <option value="">
                        (none)
                      </option>

                      {courses.map(
                        (c) => (
                          <option
                            key={
                              c.id
                            }
                            value={
                              c.id
                            }
                          >
                            {
                              c.name
                            }
                          </option>
                        ),
                      )}
                    </select>
                  </div>
                </label>
              )}

            {/* Salary pay to */}
            {selName ===
              "salary" && (
              <div className="sm:col-span-2">
                <span className={labelClass}>
                  Pay To
                </span>

                <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                  <label
                    className={`
                      flex cursor-pointer items-center gap-3
                      rounded-[10px] border px-4 py-3
                      transition
                      ${
                        payToType ===
                        "teacher"
                          ? "border-emerald-300 bg-emerald-50"
                          : "border-slate-200 bg-white hover:border-slate-300"
                      }
                    `}
                  >
                    <input
                      type="radio"
                      name="payToType"
                      value="teacher"
                      checked={
                        payToType ===
                        "teacher"
                      }
                      onChange={
                        handleChange
                      }
                      className="h-4 w-4 accent-emerald-600"
                    />

                    <UserRound
                      size={16}
                      className="text-slate-500"
                    />

                    <span className="text-[13px] font-semibold text-slate-700">
                      Teacher
                    </span>
                  </label>

                  <label
                    className={`
                      flex cursor-pointer items-center gap-3
                      rounded-[10px] border px-4 py-3
                      transition
                      ${
                        payToType ===
                        "staff"
                          ? "border-emerald-300 bg-emerald-50"
                          : "border-slate-200 bg-white hover:border-slate-300"
                      }
                    `}
                  >
                    <input
                      type="radio"
                      name="payToType"
                      value="staff"
                      checked={
                        payToType ===
                        "staff"
                      }
                      onChange={
                        handleChange
                      }
                      className="h-4 w-4 accent-emerald-600"
                    />

                    <UsersRound
                      size={16}
                      className="text-slate-500"
                    />

                    <span className="text-[13px] font-semibold text-slate-700">
                      Staff
                    </span>
                  </label>
                </div>
              </div>
            )}

            {/* Salary teacher */}
            {selName ===
              "salary" &&
              payToType ===
                "teacher" && (
                <>
                  <label className="min-w-0">
                    <span className={labelClass}>
                      Teacher
                    </span>

                    <div className="relative">
                      <UserRound
                        size={15}
                        className={iconClass}
                      />

                      <select
                        id="teacher"
                        name="teacher"
                        value={
                          form.teacher
                        }
                        onChange={
                          handleChange
                        }
                        required
                        className={`${fieldClass} pl-10 pr-8`}
                      >
                        <option value="">
                          Select…
                        </option>

                        {teachers.map(
                          (t) => (
                            <option
                              key={
                                t.id
                              }
                              value={
                                t.id
                              }
                            >
                              {
                                t.name
                              }
                            </option>
                          ),
                        )}
                      </select>
                    </div>
                  </label>

                  <label className="min-w-0">
                    <span className={labelClass}>
                      Course
                    </span>

                    <div className="relative">
                      <BookOpen
                        size={15}
                        className={iconClass}
                      />

                      <select
                        id="course"
                        name="course"
                        value={
                          form.course
                        }
                        onChange={
                          handleChange
                        }
                        required
                        className={`${fieldClass} pl-10 pr-8`}
                      >
                        <option value="">
                          Select…
                        </option>

                        {selectedTeacher?.courses.map(
                          (c) => (
                            <option
                              key={
                                c.id
                              }
                              value={
                                c.id
                              }
                            >
                              {
                                c.name
                              }
                            </option>
                          ),
                        )}
                      </select>
                    </div>
                  </label>
                </>
              )}

            {/* Salary staff */}
            {selName ===
              "salary" &&
              payToType ===
                "staff" && (
                <label className="min-w-0">
                  <span className={labelClass}>
                    Staff
                  </span>

                  <div className="relative">
                    <UsersRound
                      size={15}
                      className={iconClass}
                    />

                    <select
                      id="staff"
                      name="staff"
                      value={
                        form.staff
                      }
                      onChange={
                        handleChange
                      }
                      required
                      className={`${fieldClass} pl-10 pr-8`}
                    >
                      <option value="">
                        Select…
                      </option>

                      {staff.map(
                        (s) => (
                          <option
                            key={
                              s.id
                            }
                            value={
                              s.id
                            }
                          >
                            {
                              s.name
                            }
                          </option>
                        ),
                      )}
                    </select>
                  </div>
                </label>
              )}

            {/* Consultant */}
            {selName ===
              "consultant" && (
                <>
                  <label className="min-w-0">
                    <span className={labelClass}>
                      Teacher
                    </span>

                    <div className="relative">
                      <UserRound
                        size={15}
                        className={iconClass}
                      />

                      <select
                        id="teacher"
                        name="teacher"
                        value={
                          form.teacher
                        }
                        onChange={
                          handleChange
                        }
                        required
                        className={`${fieldClass} pl-10 pr-8`}
                      >
                        <option value="">
                          Select…
                        </option>

                        {teachers.map(
                          (t) => (
                            <option
                              key={
                                t.id
                              }
                              value={
                                t.id
                              }
                            >
                              {
                                t.name
                              }
                            </option>
                          ),
                        )}
                      </select>
                    </div>
                  </label>

                  <label className="min-w-0">
                    <span className={labelClass}>
                      Course
                    </span>

                    <div className="relative">
                      <BookOpen
                        size={15}
                        className={iconClass}
                      />

                      <select
                        id="course"
                        name="course"
                        value={
                          form.course
                        }
                        onChange={
                          handleChange
                        }
                        required
                        className={`${fieldClass} pl-10 pr-8`}
                      >
                        <option value="">
                          Select…
                        </option>

                        {selectedTeacher?.courses.map(
                          (c) => (
                            <option
                              key={
                                c.id
                              }
                              value={
                                c.id
                              }
                            >
                              {
                                c.name
                              }
                            </option>
                          ),
                        )}
                      </select>
                    </div>
                  </label>
                </>
              )}
          </div>

          {/* Footer */}
          <div
            className="
              flex items-center justify-end gap-2
              border-t border-slate-100
              bg-slate-50/50
              px-6 py-4
              max-sm:px-4
            "
          >
            <button
              type="button"
              onClick={onCancel}
              className="
                inline-flex h-10 items-center justify-center
                rounded-[9px]
                border border-emerald-200
                bg-white px-4
                text-[12px] font-semibold text-emerald-700
                transition
                hover:border-emerald-300
                hover:bg-emerald-50
                hover:text-emerald-800
                focus:outline-none
                focus:ring-2
                focus:ring-emerald-200
              "
            >
              Cancel
            </button>

            <button
              type="submit"
              className="
                inline-flex h-10 min-w-[104px]
                items-center justify-center
                rounded-[9px]
                border border-emerald-600
                bg-emerald-600 px-4
                text-[12px] font-semibold text-white
                transition
                hover:border-emerald-700
                hover:bg-emerald-700
                focus:outline-none
                focus:ring-2
                focus:ring-emerald-200
              "
            >
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
